import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {getPercent} from '@ey/utils';
import {labels} from '../../../../../util/utils';
import Tile from '../../../../Common/Tile';
import PieChart from '../../../../Common/Charts/PieChart';
import {lightColor, darkColor} from '@ey/styled-theme';
import {dashboardChartColors} from '../../../../../util/uiconstants';

const ReviewNotesByPriorityChart = ({reviewnotesData, onGraphicClick, filterKey}) => {
	const dashboardContext = useSelector((store) => store.dashboardContext);
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);
	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [chartData, setChartData] = useState([]);

	const isDark = document.getElementsByTagName('body')[0].getAttribute('motif-theme') === 'dark' ? 'dark' : 'light';
	const themeColor = isDark === 'dark' ? darkColor : lightColor;

	let winWidth = window.innerWidth;

	useEffect(() => {
		buildChartData(dashboardContext.engagements);
	}, [reviewnotesData, dashboardContext]);

	const buildChartData = (engagements) => {
		let filteredReviewNotes = [],
			engagementList = engagements || [];

		// filtering the context for dashboard context
		filteredReviewNotes = reviewnotesData.filter(
			(eng) => engagementList.some((item) => item === eng.engId) || (isFirstLoad && _.isEmpty(engagementList))
		);

		// changing first load value to false after first valid load
		if (isFirstLoad && !_.isEmpty(engagementList)) {
			setIsFirstLoad(false);
		}

		const totalNotesCount = _.sum(filteredReviewNotes.map((item) => item.rNTotalCalculatedOpen)),
			lowPriorityNotesCount = _.sum(filteredReviewNotes.map((item) => item.rNOLow)),
			mediumPriorityNotesCount = _.sum(filteredReviewNotes.map((item) => item.rNOMed)),
			highPriorityNotesCount = _.sum(filteredReviewNotes.map((item) => item.rNOHigh)),
			criticalPriorityNotesCount = _.sum(filteredReviewNotes.map((item) => item.rNOCrit));

		// building the series object for the pie chart
		const displayData = {
			total: totalNotesCount,
			series: [
				{
					name: labels.low,
					y: getPercent(lowPriorityNotesCount, totalNotesCount),
					totalCount: lowPriorityNotesCount,
					totalOverdue: _.sum(filteredReviewNotes.map((item) => item.rNOLowOD)),
					color: dashboardChartColors.orange[0]
				},
				{
					name: labels.medium,
					y: getPercent(mediumPriorityNotesCount, totalNotesCount),
					totalCount: mediumPriorityNotesCount,
					totalOverdue: _.sum(filteredReviewNotes.map((item) => item.rNOMedOD)),
					color: dashboardChartColors.orange[1]
				},
				{
					name: labels.high,
					y: getPercent(highPriorityNotesCount, totalNotesCount),
					totalCount: highPriorityNotesCount,
					totalOverdue: _.sum(filteredReviewNotes.map((item) => item.rNOHighOD)),
					color: dashboardChartColors.orange[2]
				},
				{
					name: labels.critical,
					y: getPercent(criticalPriorityNotesCount, totalNotesCount),
					totalCount: criticalPriorityNotesCount,
					totalOverdue: _.sum(filteredReviewNotes.map((item) => item.rNOCritOD)),
					color: dashboardChartColors.orange[3]
				}
			]
		};
		displayData.series = adjustPercentage(displayData.series);
		setChartData(displayData);
	};

	const adjustPercentage = (items) => {
		let totalPercentage = _.sum(items.map((data) => data.y));
		let item = _.maxBy(items, 'y');

		if (isNaN(totalPercentage)) {
			return items;
		}

		item.y = item.y + (100 - totalPercentage);

		return items;
	};

	const pieProps = {
		chartHeight: winWidth <= 1919 ? 243 : 246,
		legendWidth: 180,
		labelCenterOffset: 10,
		data: chartData.series,
		total: chartData.total,
		totalText: labels.openNotes?.toLowerCase(),
		chartCenter: [180, 80],
		onGraphicClick: (e) => {
			e.preventDefault();
			const serieLabel = e.point.options.name;
			onGraphicClick(serieLabel, filterKey);
		}
	};

	const tileProps = {
		title: labels.openReviewNotes,
		subTitle: labels.byPriorityLevel,
		isEmpty: chartData.total === 0,
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: labels.selectadditionalEntitiesToContinue
	};

	return (
		<Tile {...tileProps}>
			<PieChart {...pieProps} />
		</Tile>
	);
};

export default ReviewNotesByPriorityChart;
